/**
 * Platform notice API service - the read side of the CRA Art. 14(8) user
 * notification (SPEC-OPERATIONS RULE-019, STORY-SCR-PLATFORM-NOTICE).
 *
 * Endpoint:
 *   GET /api/v1/platform/notice   - `principal: public`, widget-flag aware
 *
 * WHY A RAW `fetch` AND NOT THE AXIOS `client`: this route is the one
 * unauthenticated read in web/src/api. The shared axios instance attaches a JWT
 * and runs the 401 refresh/redirect interceptor; both are wrong here. The bar
 * mounts in the PUBLIC widget, where there is no session to refresh and where a
 * redirect-to-login on a notice fetch would hijack a visitor's chat. The widget
 * already uses raw fetch for its unauthenticated calls (web/src/widgets/
 * embed-compat.ts), and this module follows that convention rather than the
 * JWT-bearing client used by every other module here.
 *
 * @module api/platformNotice
 * @see TASK-SCR-PLATFORM-NOTICE-BAR
 */
import { BASE_URL } from "./client";

/**
 * The public GET body. It matches TASK-SCR-NOTICE-STORE-ROUTES' public
 * response type EXACTLY - deliberately without `reason_category`, `set_by` or
 * `set_at`, which that route never returns to a public caller (RULE-019's "no
 * CVE or component detail", extended to internal metadata).
 */
export interface PlatformNoticeResponse {
  /** True only while a rolled-back release is running and must be announced. */
  active: boolean;
  /** Hungarian text, server-stored per locale (never a Lingui message id). */
  text_hu?: string;
  /** English text, server-stored per locale. */
  text_en?: string;
  /** The release the platform is running, e.g. "v1.4.4". */
  version_tag?: string;
}

/**
 * The header a PUBLIC widget uses to present its widget token.
 *
 * It is a dedicated header rather than `Authorization: Bearer` because this
 * route is also read by JWT-carrying SPAs; a dedicated header lets the widget
 * say "this is a widget token" without the server having to guess. The server
 * treats an unresolvable value as NO token, never as a denial.
 */
export const PUBLIC_WIDGET_TOKEN_HEADER = "X-Public-Widget-Token";

/**
 * Read the platform notice.
 *
 * @param publicWidgetToken - present ONLY when called from the public widget.
 *   When supplied, the server additionally requires the resolved channel's
 *   `show_platform_notice` flag before returning an active notice, which is how
 *   RULE-019's "the public widget shows it only when the tenant's channel flag
 *   enables it" is enforced SERVER-side. The caller never reads or trusts a
 *   client-visible copy of that flag.
 * @param opts.signal - abort signal, so an unmounting bar cancels its poll.
 * @throws when the request fails or the response is not 200. The caller decides
 *   what a failure means; this module never invents an "inactive" answer out of
 *   an error, because that would make a broken fetch indistinguishable from a
 *   genuinely cleared notice.
 */
export async function getPlatformNotice(
  publicWidgetToken?: string,
  opts?: { signal?: AbortSignal },
): Promise<PlatformNoticeResponse> {
  const headers: Record<string, string> = { Accept: "application/json" };
  if (publicWidgetToken) {
    headers[PUBLIC_WIDGET_TOKEN_HEADER] = publicWidgetToken;
  }

  const response = await fetch(`${BASE_URL}/platform/notice`, {
    method: "GET",
    headers,
    signal: opts?.signal,
  });
  if (!response.ok) {
    throw new Error(`platform notice request failed with status ${response.status}`);
  }
  return (await response.json()) as PlatformNoticeResponse;
}
